import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import { AvatarProvider } from "@/context/AvatarContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import HudOverlay from "@/components/HudOverlay";
import VoiceOrb from "@/components/VoiceOrb";
import HelpMenu from "@/components/HelpMenu";
import AvatarPromptModal from "@/components/AvatarPromptModal";

import Login from "@/pages/Login";
import Hub from "@/pages/Hub";
import DevPlan from "@/pages/DevPlan";
import Production from "@/pages/Production";
import ProductionDocs from "@/pages/ProductionDocs";
import Operations from "@/pages/Operations";
import ScreenSaver from "@/pages/ScreenSaver";
import Logistics from "@/pages/Logistics";
import Fleet from "@/pages/Fleet";
import Trials from "@/pages/Trials";
import TrialEditor from "@/pages/TrialEditor";
import BDPipeline from "@/pages/BDPipeline";
import AdminUsers from "@/pages/AdminUsers";
import AdminAudit from "@/pages/AdminAudit";
import AdminImport from "@/pages/AdminImport";
import BriefPage from "@/pages/BriefPage";
import RoleGate from "@/components/RoleGate";
import FullscreenToggle from "@/components/FullscreenToggle";
import KioskAutoScroll from "@/components/KioskAutoScroll";
import AntiCapture from "@/components/AntiCapture";

function AppFrame() {
    const { user } = useAuth();
    const location = useLocation();
    const isBrief = location.pathname.startsWith("/brief");
    return (
        <>
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                    path="/hub"
                    element={
                        <ProtectedRoute>
                            <Hub />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/dev-plan"
                    element={
                        <ProtectedRoute>
                            <DevPlan />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/production"
                    element={
                        <ProtectedRoute>
                            <Production />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/production/docs"
                    element={
                        <ProtectedRoute>
                            <RoleGate role="docs">
                                <ProductionDocs />
                            </RoleGate>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/production/docs/:platformId"
                    element={
                        <ProtectedRoute>
                            <RoleGate role="docs">
                                <ProductionDocs />
                            </RoleGate>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/production/docs/:platformId/:subsystem"
                    element={
                        <ProtectedRoute>
                            <RoleGate role="docs">
                                <ProductionDocs />
                            </RoleGate>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/operations"
                    element={
                        <ProtectedRoute>
                            <Operations />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/screensaver"
                    element={
                        <ProtectedRoute>
                            <ScreenSaver />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/logistics"
                    element={
                        <ProtectedRoute>
                            <Logistics />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/fleet"
                    element={
                        <ProtectedRoute>
                            <Fleet />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/trials"
                    element={
                        <ProtectedRoute>
                            <Trials />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/trials/:id"
                    element={
                        <ProtectedRoute>
                            <TrialEditor />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/bd"
                    element={
                        <ProtectedRoute>
                            <RoleGate role="bd">
                                <BDPipeline />
                            </RoleGate>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/users"
                    element={
                        <ProtectedRoute>
                            <RoleGate role="admin">
                                <AdminUsers />
                            </RoleGate>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/import"
                    element={
                        <ProtectedRoute>
                            <RoleGate role="admin">
                                <AdminImport />
                            </RoleGate>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/admin/audit"
                    element={
                        <ProtectedRoute>
                            <AdminAudit />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/brief"
                    element={
                        <ProtectedRoute>
                            <BriefPage />
                        </ProtectedRoute>
                    }
                />
                <Route path="/" element={<Navigate to="/hub" replace />} />
                <Route path="*" element={<Navigate to="/hub" replace />} />
            </Routes>
            {user && !isBrief && <HudOverlay />}
            {user && !isBrief && <FullscreenToggle />}
            {user && !isBrief && <KioskAutoScroll />}
            {user && !isBrief && <AntiCapture />}
            {user && !isBrief && <VoiceOrb />}
            {user && !isBrief && <HelpMenu />}
            {user && !isBrief && <AvatarPromptModal />}
        </>
    );
}

function App() {
    return (
        <div className="App min-h-screen dark bg-virtus-bg text-white">
            <AuthProvider>
                <RealtimeProvider>
                    <AvatarProvider>
                        <BrowserRouter>
                            <AppFrame />
                        </BrowserRouter>
                    </AvatarProvider>
                </RealtimeProvider>
            </AuthProvider>
            <Toaster
                position="top-right"
                theme="dark"
                toastOptions={{
                    style: {
                        background: "#0A0A0A",
                        color: "#FFFFFF",
                        border: "1px solid #FF5500",
                        borderRadius: "0px",
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: "12px",
                    },
                }}
            />
        </div>
    );
}

export default App;
